import React, { memo, useEffect, useMemo, useRef } from 'react';
import katex from 'katex';
import renderMathInElement from 'katex/dist/contrib/auto-render';
import 'katex/dist/katex.min.css';
import './katex-text.scss';

const MATH_PATTERN = /\$\$([^$]+)\$\$/g;

export function splitKatex(text) {
  if (typeof text !== 'string' || text === '') return [];
  const pattern = new RegExp(MATH_PATTERN);
  const parts = [];
  let cursor = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) parts.push({ text: text.slice(cursor, match.index) });
    parts.push({ math: match[1], raw: match[0] });
    cursor = pattern.lastIndex;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor) });
  return parts;
}

// Keep a formula whole when an existing character limit truncates a title.
export function truncateKatex(text, max, suffix = '..') {
  if (typeof text !== 'string' || text.length <= max) return text;
  let result = '';
  for (const part of splitKatex(text)) {
    const value = part.raw || part.text;
    const remaining = max - result.length;
    if (value.length <= remaining) {
      result += value;
    } else {
      if (part.math == null) result += value.slice(0, Math.max(0, remaining));
      break;
    }
  }
  return result + suffix;
}

// KaTeX 가 내보내는 기호 중 문자 메시지(EUC-KR)로 보낼 수 없는 것을 같은 뜻의 문자로 바꾼다.
const PLAIN_SYMBOLS = {
  '−': '-', '⋅': '·', '∙': '·', '∗': '*', '∣': '|', '∘': '○', '◯': '○', '⋯': '…',
  '≈': '≒', '≦': '≤', '≧': '≥', '⩽': '≤', '⩾': '≥', '∅': 'Ø',
};
const PLAIN_SYMBOL_PATTERN = new RegExp(`[${Object.keys(PLAIN_SYMBOLS).join('')}]`, 'g');
const SUPERSCRIPTS = new Map([['2', '²'], ['3', '³']]);
const ROOT_INDEXES = new Map([['2', ''], ['3', '³'], ['4', '⁴'], ['n', 'ⁿ']]);
const TOKENS = ['mi', 'mn', 'mo', 'ms', 'mtext'];
// 분수 앞뒤가 이 문자면 괄호 없이 1/2+1/3 처럼 적어도 뜻이 달라지지 않는다.
const LOOSE_BEFORE = '+-=<>≤≥≠≒,;([{';
const LOOSE_AFTER = '+-=<>≤≥≠≒,;)]}';

const unwrap = (node) =>
  (node.localName === 'mrow' || node.localName === 'mstyle') && node.children.length === 1
    ? unwrap(node.children[0])
    : node;
const isToken = (node) => TOKENS.includes(node.localName) && node.children.length === 0;
const isUpright = (node) => node.localName === 'mi' && node.getAttribute('mathvariant') === 'normal';
const isFraction = (node) => unwrap(node).localName === 'mfrac';
const accentOf = (node) => (unwrap(node).localName === 'mover' ? unwrap(node).children[1].textContent : '');
const isDigits = (text) => /^\d+$/.test(text);

const isWrapped = (text) => {
  if (text[0] !== '(') return false;
  let depth = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === '(') depth += 1;
    if (text[i] === ')') depth -= 1;
    if (depth === 0) return i === text.length - 1;
  }
  return false;
};

// 수·글자 하나나 cm 처럼 곧게 쓴 낱말이 아닌 식은 괄호로 묶어 √(a+b), (a+b)/c 처럼 범위를 남긴다.
const group = (node, text) => {
  const term = unwrap(node);
  const whole =
    isToken(term) ||
    (term.localName === 'mrow' && Array.from(term.children).every(isUpright)) ||
    isWrapped(text.trim());
  return whole ? text : `(${text})`;
};

const superscript = (node, text) => {
  if (text === '○') return '°';
  if (/^[′″]+$/.test(text)) return text;
  return SUPERSCRIPTS.get(text) || `^${group(node, text)}`;
};

function joinRow(children, plain) {
  const pieces = plain.slice();
  for (let i = 0; i < children.length; i += 1) {
    if (isDigits(pieces[i]) && ['˙', '‾', 'ˉ'].includes(accentOf(children[i]))) {
      // 순환소수는 점 찍힌 첫 자리부터 끝 자리까지를 되풀이해 0.333… 으로 적는다.
      let last = i;
      for (let k = i + 1; k < children.length && isDigits(pieces[k]); k += 1) {
        if (accentOf(children[k]) === '˙') last = k;
      }
      const period = pieces.slice(i, last + 1).join('');
      pieces.fill('', i, last + 1);
      pieces[i] = `${period.repeat(period.length === 1 ? 3 : 2)}…`;
      i = last;
    } else if (isFraction(children[i])) {
      // 대분수는 2 1/3 으로 적고, 곱셈·나눗셈이나 다른 식과 붙은 분수는 6÷(2/3) 처럼 묶는다.
      const mixed = i > 0 && unwrap(children[i - 1]).localName === 'mn' && isDigits(pieces[i - 1]);
      const start = mixed ? i - 1 : i;
      const before = pieces.slice(0, start).join('').trim().slice(-1);
      const after = pieces.slice(i + 1).join('').trim().charAt(0);
      if (mixed) pieces[i] = ` ${pieces[i]}`;
      if ((before && !LOOSE_BEFORE.includes(before)) || (after && !LOOSE_AFTER.includes(after))) {
        pieces[start] = `(${pieces[start]}`;
        pieces[i] = `${pieces[i]})`;
      }
    }
  }
  return pieces.join('');
}

function mathmlToPlainText(node) {
  const children = Array.from(node.children);
  const plain = children.map(mathmlToPlainText);
  if (isToken(node)) {
    // KaTeX 가 함수 적용·숨은 곱셈 자리에 넣는 보이지 않는 문자(U+2061~4)는 뺀다.
    return node.textContent
      .replace(/[\u2061-\u2064]/g, '')
      .replace(PLAIN_SYMBOL_PATTERN, (symbol) => PLAIN_SYMBOLS[symbol]);
  }
  switch (node.localName) {
    case 'mspace':
      return ' ';
    case 'mphantom':
      return '';
    case 'menclose':
      return joinRow(children, plain).trim() || '□';
    case 'msqrt':
      return `√${group(children[0], plain[0])}`;
    case 'mroot':
      return ROOT_INDEXES.has(plain[1])
        ? `${ROOT_INDEXES.get(plain[1])}√${group(children[0], plain[0])}`
        : `${group(children[0], plain[0])}^(1/${group(children[1], plain[1])})`;
    case 'mfrac':
      return `${group(children[0], plain[0])}/${group(children[1], plain[1])}`;
    case 'msup':
      return group(children[0], plain[0]) + superscript(children[1], plain[1]);
    case 'msub':
      return `${group(children[0], plain[0])}_${group(children[1], plain[1])}`;
    case 'msubsup':
      return `${group(children[0], plain[0])}_${group(children[1], plain[1])}${superscript(children[2], plain[2])}`;
    // 선분의 윗줄, 화살표 같은 장식은 한 줄로 옮길 수 없어 밑 식만 남긴다.
    case 'mover':
    case 'munder':
    case 'munderover':
      return plain[0];
    case 'mtable':
      return plain.join(', ');
    case 'mtr':
      return plain.join(' ');
    default:
      return joinRow(children, plain);
  }
}

function mathToPlainText(math) {
  try {
    const markup = katex.renderToString(math, { output: 'mathml' });
    const semantics = new DOMParser().parseFromString(markup, 'text/html').querySelector('semantics');
    return mathmlToPlainText(semantics.firstElementChild).replace(/\s+/g, ' ').trim();
  } catch (error) {
    // KaTeX 가 해석하지 못한 수식은 원문을 그대로 둔다.
    return math.trim();
  }
}

// 문자 메시지처럼 수식을 그릴 수 없는 곳에 넣을 수 있게 $$수식$$ 을 √2, 2/3 같은 한 줄 표기로 바꾼다.
export function katexToPlainText(text) {
  if (typeof text !== 'string') return text;
  return splitKatex(text)
    .map((part) => (part.math != null ? mathToPlainText(part.math) : part.text))
    .join('');
}

const MathText = memo(({ math }) => {
  const markup = useMemo(
    () => katex.renderToString(math, { throwOnError: false, displayMode: false }),
    [math]
  );
  return <span className="queture-math" dangerouslySetInnerHTML={{ __html: markup }} />;
});

// Existing formatting/highlighting may transform text, but never a formula token.
const KatexText = ({ text, renderText, ...spanProps }) => {
  const parts = useMemo(() => splitKatex(text), [text]);
  if (typeof text !== 'string') return text == null ? null : text;
  const renderPlainText = (part, index) => {
    let rendered = renderText ? renderText(part.text) : part.text;
    if (renderText && typeof rendered === 'string') {
      // Trim-based legacy formatters must not join the words around a formula.
      if (index > 0 && parts[index - 1].math != null) {
        rendered = part.text.match(/^\s*/)[0] + rendered.replace(/^\s*/, '');
      }
      if (index + 1 < parts.length && parts[index + 1].math != null) {
        rendered = rendered.replace(/\s*$/, '') + part.text.match(/\s*$/)[0];
      }
    }
    return rendered;
  };
  const content = parts.map((part, index) =>
    part.math != null ? (
      <MathText key={index} math={part.math} />
    ) : (
      <React.Fragment key={index}>
        {renderPlainText(part, index)}
      </React.Fragment>
    )
  );
  return Object.keys(spanProps).length ? <span {...spanProps}>{content}</span> : <>{content}</>;
};

// Prologues already contain HTML. Render formulas only in their text nodes.
export const KatexHtml = ({ html, as: Tag = 'span', ...props }) => {
  const ref = useRef(null);
  const preparedHtml = useMemo(() => (html || '').replace(new RegExp(MATH_PATTERN), (formula) =>
    formula
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .replace(/&(?!(?:#\d+|#x[\da-f]+|[a-z][\da-z]+);)/gi, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  ), [html]);
  useEffect(() => {
    if (!ref.current) return;
    renderMathInElement(ref.current, {
      delimiters: [{ left: '$$', right: '$$', display: false }],
      throwOnError: false,
    });
  }, [preparedHtml]);
  return <Tag {...props} ref={ref} dangerouslySetInnerHTML={{ __html: preparedHtml }} />;
};

export default KatexText;
